import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FaPlus, FaSave, FaTrash, FaSync, FaArrowUp, FaArrowDown, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import api from '../../../api/apiClient';

const initialFaqState = {
  id: null,
  question: '',
  answer_html: '',
  audience: 'END_USER',
  is_published: false
};

const FaqAnswerEditor = ({ value, onChange, disabled }) => {
  const isUpdatingFromProps = useRef(false);
  
  const editor = useEditor(
    {
      extensions: [StarterKit],
      content: value || '',
      editable: !disabled,
      onUpdate({ editor }) {
        if (isUpdatingFromProps.current) return;
        const html = editor.getHTML();
        onChange(html);
      }
    },
    []
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      isUpdatingFromProps.current = true;
      editor.commands.setContent(value || '', false);
      setTimeout(() => {
        isUpdatingFromProps.current = false;
      }, 0);
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div className="border rounded p-2 bg-light text-muted small">Loading editor...</div>
    );
  }

  return (
    <div className="border rounded" style={{ minHeight: '180px' }}>
      <EditorContent editor={editor} className="p-2" />
    </div>
  );
};

const FAQManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [faqs, setFaqs] = useState([]);

  const [filterAudience, setFilterAudience] = useState('');
  const [search, setSearch] = useState('');
  const [filterPublished, setFilterPublished] = useState('all');

  const [faqForm, setFaqForm] = useState(initialFaqState);

  const [savingFaq, setSavingFaq] = useState(false);

  const [selectedFaqId, setSelectedFaqId] = useState(null);

  const normalize = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const loadFaqs = useCallback(async () => {
    const params = {};
    if (filterAudience) params.audience = filterAudience;
    if (search && search.trim()) params.search = search.trim();
    if (filterPublished === 'published') params.is_published = '1';
    if (filterPublished === 'draft') params.is_published = '0';

    const response = await api.faqs.list(params);
    if (response.data?.success) {
      setFaqs(response.data.data || []);
    }
  }, [filterAudience, search, filterPublished]);

  const fetchAll = useCallback(async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) setLoading(true);
      await loadFaqs();
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [loadFaqs]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return;
    setRefreshing(true);
    try {
      await fetchAll({ showLoader: false });
    } finally {
      setRefreshing(false);
    }
  }, [fetchAll, loading, refreshing]);

  const sortedFaqs = useMemo(() => {
    return [...faqs].sort((a, b) => a.position - b.position || new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
  }, [faqs]);

  const handleFaqFormChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setFaqForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleFaqAnswerChange = useCallback((html) => {
    setFaqForm((prev) => ({
      ...prev,
      answer_html: html || ''
    }));
  }, []);

  const handleSaveCategory = useCallback(async (event) => {
    event.preventDefault();
  }, []);

  const changeFaqOrder = useCallback(async (faqId, direction) => {
    const items = sortedFaqs.map((f) => ({ id: f.id, audience: f.audience, position: f.position }));
    const index = items.findIndex((x) => x.id === faqId);
    if (index === -1) return;

    if (direction === 'up' && index > 0) {
      const tmp = items[index - 1].position;
      items[index - 1].position = items[index].position;
      items[index].position = tmp;
    }
    if (direction === 'down' && index < items.length - 1) {
      const tmp = items[index + 1].position;
      items[index + 1].position = items[index].position;
      items[index].position = tmp;
    }

    await api.faqs.reorder(items);
    await loadFaqs();
  }, [sortedFaqs, loadFaqs]);

  const handleEditFaq = useCallback((faq) => {
    if (!faq) return;
    setSelectedFaqId(faq.id);
    setFaqForm({
      id: faq.id,
      question: faq.question || '',
      answer_html: faq.answer_html || '',
      audience: faq.audience || 'END_USER',
      is_published: !!faq.is_published
    });
  }, []);

  const resetFaqForm = useCallback(() => {
    setSelectedFaqId(null);
    setFaqForm(initialFaqState);
  }, []);

  const handleSaveFaq = useCallback(async (event) => {
    event.preventDefault();

    const question = normalize(faqForm.question);
    const answer_html = faqForm.answer_html || '';
    if (!question || !answer_html.trim()) return;

    setSavingFaq(true);
    try {
      const payload = {
        question: normalize(faqForm.question),
        answer_html: faqForm.answer_html,
        audience: faqForm.audience || 'END_USER',
        is_published: !!faqForm.is_published
      };

      if (faqForm.id) {
        await api.faqs.update(faqForm.id, payload);
      } else {
        await api.faqs.create(payload);
      }

      await loadFaqs();
      resetFaqForm();
    } finally {
      setSavingFaq(false);
    }
  }, [faqForm, loadFaqs, normalize, resetFaqForm]);

  const handleDeleteFaq = useCallback(async (faqId) => {
    if (!faqId) return;
    await api.faqs.remove(faqId);
    if (selectedFaqId === faqId) {
      resetFaqForm();
    }
    await loadFaqs();
  }, [loadFaqs, resetFaqForm, selectedFaqId]);

  const handleFiltersChange = useCallback(async () => {
    await loadFaqs();
  }, [loadFaqs]);

  useEffect(() => {
    handleFiltersChange();
  }, [handleFiltersChange, filterAudience, search, filterPublished]);

  return (
    <div className="p-3 sm:p-6 md:p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/site-management')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h3 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">FAQ Management</h3>
            <p className="text-sm text-gray-600 mt-1">Manage frequently asked questions by audience, order and publish status.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            {refreshing ? 'Refreshing...' : (<><FaSync className="inline-block mr-2"/> Refresh</>)}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-4">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Search</label>
            <div className="flex items-center bg-white border border-purple-200 rounded-lg px-3 py-2">
              <FaSearch className="text-gray-400 mr-2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search questions or answers..." className="w-full outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Audience</label>
            <select value={filterAudience} onChange={(e) => setFilterAudience(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="">All audiences</option>
              <option value="ORGANIZATION">Organization</option>
              <option value="ORG_ADMIN">Organization Admin</option>
              <option value="END_USER">End User</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
            <select value={filterPublished} onChange={(e) => setFilterPublished(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h6 className="text-lg font-bold mb-3">FAQs</h6>
          {loading ? (
            <div className="text-center py-6"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div></div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="text-left py-3 px-4">Question</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-purple-50">
                  {sortedFaqs.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center text-gray-500 py-6">No FAQs found. Use the editor below to create your first FAQ.</td>
                    </tr>
                  ) : (
                    sortedFaqs.map((item, index) => (
                      <tr key={item.id}>
                        <td className="py-3 px-4 align-top">
                          <button onClick={() => handleEditFaq(item)} className="text-left text-sm font-medium text-purple-800 hover:underline">{item.question}</button>
                          {item.is_published && <span className="ml-2 inline-block px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-semibold">Published</span>}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="d-flex gap-1 justify-content-end">
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeFaqOrder(item.id, 'up')}
                              disabled={index === 0}
                              title="Move Up"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowUp className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => changeFaqOrder(item.id, 'down')}
                              disabled={index === sortedFaqs.length - 1}
                              title="Move Down"
                              className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                            >
                              <FaArrowDown className="w-5 h-5" />
                            </Button>
                            <Button
                              bsPrefix="custom-hamburger"
                              onClick={() => handleDeleteFaq(item.id)}
                              title="Delete FAQ"
                              className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                            >
                              <FaTrash className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <h6 className="text-lg font-bold mb-3">{selectedFaqId ? 'Edit FAQ' : 'New FAQ'}</h6>
          <form onSubmit={handleSaveFaq} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Question</label>
              <input type="text" name="question" value={faqForm.question} onChange={handleFaqFormChange} disabled={savingFaq} className="w-full px-4 py-2 border border-purple-200 rounded-lg" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700">Audience</label>
                <select name="audience" value={faqForm.audience} onChange={handleFaqFormChange} disabled={savingFaq} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
                  <option value="ORGANIZATION">Organization</option>
                  <option value="ORG_ADMIN">Organization Admin</option>
                  <option value="END_USER">End User</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" name="is_published" checked={faqForm.is_published} onChange={handleFaqFormChange} disabled={savingFaq} className="w-4 h-4" />
                  <span>Published</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
              <FaqAnswerEditor value={faqForm.answer_html} onChange={handleFaqAnswerChange} disabled={savingFaq} />
              <p className="text-xs text-gray-500 mt-2">This rich text editor stores content as HTML. Formatting such as paragraphs, bold, and lists will be preserved.</p>
            </div>

            <div className="flex justify-end items-center gap-3">
              {selectedFaqId && (
                <Button
                  bsPrefix="custom-hamburger"
                  type="button"
                  onClick={resetFaqForm}
                  disabled={savingFaq}
                  className="border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                >
                  Cancel
                </Button>
              )}
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                disabled={savingFaq}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2 flex items-center gap-2"
              >
                {savingFaq ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Save FAQ
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FAQManager;
